import { dissocPath, map, mergeRight } from 'ramda';
import { deepEquals } from '@nike/ciclp-utils';
import createReducer from '../../utils/createReducer';
import {
  CARD_TYPES,
  EDITORIAL_TYPES,
  LOCAL_NAV_PLACEHOLDER_CARD_ID,
} from '../../constants';
import {
  pageThreadApiActions,
  urlGeneratorApiActions,
  bannerFeedApiActions,
  pathToRootActions,
  productStatusesApiActions,
  dynamicContentActions,
  snkrsDropsActions,
  dynamicLocalNavActions,
  cmsCollectionActions,
  contentFeatureActions,
  variantPatchesActions,
} from '../../actions';

const defaultState = {
  banner: {},
  buyingToolsIngredients: [],
  cards: {},
  pageData: {
    showBanner: true,
  },
  requestResponse: [],
  urls: [],
};

export const dataReducer = createReducer(defaultState, {
  [pageThreadApiActions.success.type]: (state, action) => ({
    ...state,
    ...action.payload.entities,
  }),
  [urlGeneratorApiActions.success.type]: (state, { payload } = {}) => ({
    ...state,
    urls: [...state.urls, ...payload],
  }),
  [productStatusesApiActions.success.type]: (state, { payload } = {}) => ({
    ...state,
    buyingToolsIngredients: [...state.buyingToolsIngredients, ...payload],
  }),
  [bannerFeedApiActions.success.type]: (state, { payload }) => ({
    ...state,
    banner: {
      nodes: payload,
    },
  }),
  [pathToRootActions.set.type]: (state, { payload: pathToRoot }) => ({
    ...state,
    pathToRoot,
  }),
  [dynamicContentActions.insertItems.type]: (state, { payload } = {}) => {
    // Handle null payload gracefully (e.g., when API call fails due to invalid params)
    if (!payload) {
      return state;
    }

    return {
      ...state,
      cards: map(card => {
        const areEditorialattributeIdsAllChanged =
          !deepEquals(payload.attributeIdsAll, card.attributeIdsAll) ||
          !deepEquals(payload.attributeIds, card.attributeIds) ||
          !deepEquals(payload.excludeAttributeIds, card.excludeAttributeIds);

        if (
          !Object.values(EDITORIAL_TYPES).includes(card.containerType) ||
          areEditorialattributeIdsAllChanged
        )
          return card;
        const { slides, pagination } = payload;

        return {
          ...card,
          pagination,
          slides,
        };
      }, state.cards),
    };
  },
  [dynamicContentActions.insertFeatured.type]: (state, { payload }) => ({
    ...state,
    cards: map(({ featured, ...card }) =>
      !Object.values(EDITORIAL_TYPES).includes(card.containerType) ||
      payload.collectionId !== featured
        ? card
        : {
            ...card,
            featuredCard: payload.featured,
          },
    )(state.cards),
  }),
  [snkrsDropsActions.updateItems.type]: (state, { payload = {} }) => ({
    ...state,
    cards: map(card => {
      const snkrsCard = payload.find(({ cardId }) => card.id === cardId);
      return snkrsCard
        ? {
            ...card,
            ...{
              slides: snkrsCard.slides,
              slidesUpcoming: snkrsCard.slidesUpcoming,
            },
          }
        : card;
    }, state.cards),
  }),
  [dynamicLocalNavActions.removePlaceholder.type]: dissocPath([
    'cards',
    LOCAL_NAV_PLACEHOLDER_CARD_ID,
  ]),
  [dynamicLocalNavActions.insert.type]: (state, { payload } = {}) => {
    const shouldUpdateH1 =
      !!payload?.title &&
      !Object.values(state.cards).some(
        c => c.containerType === CARD_TYPES.TITLE,
      );

    return {
      ...state,
      cards: {
        ...state.cards,
        local_nav_placeholder: mergeRight(
          state.cards[LOCAL_NAV_PLACEHOLDER_CARD_ID],
          payload,
        ),
      },
      layoutItems: map(({ withH1, ...item }) =>
        shouldUpdateH1
          ? mergeRight(
              item,
              (withH1 || item?.data === LOCAL_NAV_PLACEHOLDER_CARD_ID) &&
                shouldUpdateH1 && {
                  withH1: item?.data === LOCAL_NAV_PLACEHOLDER_CARD_ID,
                },
            )
          : { ...item, withH1 },
      )(state.layoutItems),
    };
  },
  [cmsCollectionActions.loaded.type]: (
    state,
    { payload: cmsCollectionCards },
  ) => {
    return {
      ...state,
      cards: {
        ...state.cards,
        ...cmsCollectionCards,
      },
    };
  },
  [contentFeatureActions.setContentFeatures.type]: (
    state,
    { payload = {} },
  ) => {
    return {
      ...state,
      contentFeatures: payload,
    };
  },
  [variantPatchesActions.setRenderedVariantId.type]: (state, { payload }) => {
    return {
      ...state,
      renderedVariantId: payload,
    };
  },
});
